import type {
  AcceptInvitationRequestV1,
  LoginNextStepV1,
  LoginRequestV1,
  PasswordResetConfirmV1,
  PasswordResetRequestV1,
  SessionStartedResponseV1,
  SsoStartRequestV1,
} from "@strust/contracts";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useApiClient } from "../../../shared/api/api-provider.tsx";
import {
  acceptInvitationEndpoint,
  beginLoginTwoFactorSetupEndpoint,
  confirmLoginTwoFactorSetupEndpoint,
  inspectInvitationEndpoint,
  loginCodeEndpoint,
  loginEndpoint,
  loginNewPasswordEndpoint,
  logoutEndpoint,
  passwordResetConfirmEndpoint,
  passwordResetRequestEndpoint,
  ssoStartEndpoint,
} from "./endpoints.ts";

/**
 * What a password login or an invitation acceptance led to: a session, or the step it still needs
 * (change add-console-account-settings, design D5).
 */
export type SignInOutcome =
  { readonly kind: "session" } | { readonly kind: "step"; readonly step: LoginNextStepV1 };

function outcomeOf(
  answer: SessionStartedResponseV1 | { readonly next_step: LoginNextStepV1 },
  started: (csrfToken: string) => void,
): SignInOutcome {
  if ("next_step" in answer) return { kind: "step", step: answer.next_step };
  started(answer.csrf_token);
  return { kind: "session" };
}

/** Starts a console session: keeps the CSRF token for unsafe calls and drops any earlier user's cache. */
function useSessionStarted() {
  const client = useApiClient();
  const queryClient = useQueryClient();
  return (csrfToken: string) => {
    client.setCsrfToken(csrfToken);
    queryClient.clear();
  };
}

export function useLogin() {
  const client = useApiClient();
  const started = useSessionStarted();
  return useMutation({
    mutationFn: async (body: LoginRequestV1) =>
      outcomeOf(await client.call(loginEndpoint, { body }), started),
  });
}

/** The authenticator code of a password login (the login-step cookie travels by itself). */
export function useLoginCode() {
  const client = useApiClient();
  const started = useSessionStarted();
  return useMutation({
    mutationFn: (code: string) => client.call(loginCodeEndpoint, { body: { code } }),
    onSuccess: (session) => {
      started(session.csrf_token);
    },
  });
}

/**
 * The new password that replaces a temporary one (the login-step cookie travels by itself): a
 * session, or the next step (the mandatory set-up of an administrator).
 */
export function useLoginNewPassword() {
  const client = useApiClient();
  const started = useSessionStarted();
  return useMutation({
    mutationFn: async (newPassword: string) =>
      outcomeOf(
        await client.call(loginNewPasswordEndpoint, { body: { new_password: newPassword } }),
        started,
      ),
  });
}

/**
 * The mandatory set-up of an authenticator before the first session. The secret is held only by
 * this mutation (never cached: `gcTime: 0`) and dropped once the step ends.
 */
export function useBeginLoginTwoFactorSetup() {
  const client = useApiClient();
  return useMutation({
    mutationFn: () => client.call(beginLoginTwoFactorSetupEndpoint, {}),
    gcTime: 0,
  });
}

export function useConfirmLoginTwoFactorSetup() {
  const client = useApiClient();
  const started = useSessionStarted();
  return useMutation({
    mutationFn: (code: string) =>
      client.call(confirmLoginTwoFactorSetupEndpoint, { body: { code } }),
    onSuccess: (session) => {
      started(session.csrf_token);
    },
  });
}

/** Resolves to the identity provider's authorization URL; the caller redirects the browser. */
export function useSsoStart() {
  const client = useApiClient();
  return useMutation({
    mutationFn: (body: SsoStartRequestV1) => client.call(ssoStartEndpoint, { body }),
  });
}

/**
 * Ends the session. Whatever the answer, this browser no longer holds a usable session: the token
 * is dropped, and the caller clears the cache after leaving the signed-in screens.
 */
export function useLogout() {
  const client = useApiClient();
  return useMutation({
    mutationFn: () => client.call(logoutEndpoint, {}),
    onSettled: () => {
      client.setCsrfToken(undefined);
    },
  });
}

/**
 * Reads an invitation link. `enabled: false` once the link is being accepted: starting the session
 * clears the query cache, and a still-mounted observer would otherwise read the consumed link again
 * (answered `409 invitation_used`).
 */
export function useInvitation(token: string, enabled = true) {
  const client = useApiClient();
  return useQuery({
    queryKey: ["invitation", token],
    queryFn: ({ signal }) => client.call(inspectInvitationEndpoint, { params: { token }, signal }),
    enabled,
    retry: false,
    // The link is single use: never served from a cache or refetched behind the user's back.
    gcTime: 0,
    staleTime: Infinity,
  });
}

export function useAcceptInvitation(token: string) {
  const client = useApiClient();
  const started = useSessionStarted();
  return useMutation({
    mutationFn: async (body: AcceptInvitationRequestV1) =>
      outcomeOf(await client.call(acceptInvitationEndpoint, { params: { token }, body }), started),
  });
}

/** Asks for a password reset code: the answer is the same for every email (no account enumeration). */
export function useRequestPasswordReset() {
  const client = useApiClient();
  return useMutation({
    mutationFn: (body: PasswordResetRequestV1) =>
      client.call(passwordResetRequestEndpoint, { body }),
  });
}

/** Sets a new password with the emailed code; no session starts (the user signs in afterwards). */
export function useConfirmPasswordReset() {
  const client = useApiClient();
  return useMutation({
    mutationFn: (body: PasswordResetConfirmV1) =>
      client.call(passwordResetConfirmEndpoint, { body }),
  });
}
